import React, { useState, useEffect } from 'react';
import { 
  X, 
  Search, 
  Star,
  Check,
  User,
  Building,
  MapPin,
  Mail,
  Phone,
  Info,
  ChevronDown,
  Send,
  TrendingUp,
  Zap,
  Award,
  XCircle
} from 'lucide-react';
import AddButton from './shared/AddButton';

const AddSubmissionModal = ({ isOpen, onClose, selectedJob, onSubmissionCreated, editingSubmission }) => {
  const [formData, setFormData] = useState({
    addedBy: 'Ramkumar Sankaranarayanan',
    jobId: selectedJob?.id || '',
    candidateId: '',
    status: 'sourced',
    source: '',
    payRate: '',
    billRate: '',
    salary: '',
    comments: ''
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (editingSubmission) {
      setFormData({
        addedBy: editingSubmission.submitted_by_name || editingSubmission.recruiter_name || 'Ramkumar Sankaranarayanan',
        jobId: editingSubmission.job_id || '',
        candidateId: editingSubmission.candidate_id || '',
        status: editingSubmission.current_status || 'sourced',
        source: editingSubmission.submission_source || '',
        payRate: editingSubmission.pay_rate || '',
        billRate: editingSubmission.bill_rate || '',
        salary: editingSubmission.salary_expectation || '',
        comments: editingSubmission.submission_notes || ''
      });
      // Pre-select candidate
      if (editingSubmission.candidate_id) {
        setSelectedCandidates([{
          id: editingSubmission.candidate_id,
          name: editingSubmission.candidate_name,
          email: editingSubmission.candidate_email
        }]);
      }
    } else {
      // Reset form for new submission
      setFormData({
        addedBy: 'Ramkumar Sankaranarayanan',
        jobId: selectedJob?.id || '',
        candidateId: '',
        status: 'sourced',
        source: '',
        payRate: '',
        billRate: '',
        salary: '',
        comments: ''
      });
      setSelectedCandidates([]);
    }
  }, [editingSubmission, selectedJob]);

  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [matchedCandidates, setMatchedCandidates] = useState([]);  // New: AI-matched candidates
  const [showMatchedCandidates, setShowMatchedCandidates] = useState(false);  // New: toggle for AI matches
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [showCandidateResults, setShowCandidateResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);  // New: loading state for AI matching
  const [sortBy, setSortBy] = useState('match_score');  // New: sort option (match_score, name, recent)
  // submittingCandidates state now handled by AddButton component

  const submissionStatuses = [
    'sourced', 'screened', 'submitted', 'client_reviewing', 'client_accepted', 
    'client_rejected', 'interview_scheduled', 'interview_scheduled', 'interview_scheduled',
    'interview_passed', 'interview_failed', 'offer_made', 'offer_accepted', 
    'offer_rejected', 'placed', 'started', 'rejected', 'withdrawn'
  ];

  const sources = [
    'LinkedIn', 'Indeed', 'Monster', 'Dice', 'CareerBuilder', 'Glassdoor',
    'Company Website', 'Referral', 'Direct', 'Recruiter Network', 'Job Board'
  ];

  useEffect(() => {
    if (isOpen) {
      fetchJobs();
      fetchCandidates();
      if (selectedJob) {
        setFormData(prev => ({ ...prev, jobId: selectedJob.id }));
        setJobSearchTerm(selectedJob.title);
        fetchMatchedCandidates(selectedJob.id);  // Fetch AI-matched candidates
      }
    }
  }, [isOpen, selectedJob]);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        setCandidates(data);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchMatchedCandidates = async (jobId) => {
    if (!jobId) return;
    
    setLoadingMatches(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/matched-candidates`);
      if (response.ok) {
        const data = await response.json();
        setMatchedCandidates(data.matched_candidates || []);
        setShowMatchedCandidates(true);
        console.log('✅ Fetched matched candidates:', data.matched_candidates?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching matched candidates:', error);
      setMatchedCandidates([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-orange-100 text-orange-800 border-orange-300';
  };

  const getMatchScoreIcon = (score) => {
    if (score >= 80) return <Award className="w-4 h-4" />;
    if (score >= 60) return <TrendingUp className="w-4 h-4" />;
    return <Zap className="w-4 h-4" />;
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
    job.id.toString().includes(jobSearchTerm)
  );

  const filteredCandidates = candidates.filter(candidate => {
    const fullName = `${candidate.first_name} ${candidate.last_name}`.toLowerCase();
    const email = candidate.email?.toLowerCase() || '';
    return fullName.includes(candidateSearchTerm.toLowerCase()) ||
           email.includes(candidateSearchTerm.toLowerCase());
  });

  const handleJobSelect = (job) => {
    setFormData(prev => ({ ...prev, jobId: job.id }));
    setJobSearchTerm(`${job.id}: ${job.title}`);
    setShowJobDropdown(false);
    fetchMatchedCandidates(job.id);  // Fetch AI-matched candidates when job is selected
  };

  const handleCandidateSelect = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData(prev => ({ ...prev, candidateId: candidate.id }));
    setCandidateSearchTerm(`${candidate.first_name} ${candidate.last_name}`);
    setShowCandidateResults(false);
  };

  const handleSubmitCandidate = async (candidate) => {
    if (!formData.jobId) {
      throw new Error('Please select a job first.');
    }
    
    const submissionData = {
      candidate_id: candidate.id,
      job_id: formData.jobId,
      job_title: jobs.find(j => j.id === formData.jobId)?.title || 'Unknown Job',
      recruiter_id: 'current-user-id', // Would come from auth context
      recruiter_name: formData.addedBy,
      initial_status: formData.status,
      submission_source: formData.source || 'manual',
      submission_notes: formData.comments,
      priority_level: 'normal',
      match_score: 0.85 // Default match score
    };

    // Determine if we're creating or updating
    const isEditing = editingSubmission && editingSubmission.id;
    const url = isEditing 
      ? `${process.env.REACT_APP_BACKEND_URL}/api/submissions/${editingSubmission.id}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/submissions`;
    const method = isEditing ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(isEditing ? {
        new_status: formData.status,
        add_internal_note: formData.comments,
        salary_expectation: formData.salary ? { amount: parseFloat(formData.salary), currency: 'USD' } : null,
        priority_level: 'normal'
      } : submissionData)
    });

    if (response.ok) {
      const updatedSubmission = await response.json();
      if (!isEditing) {
        setSelectedCandidates(prev => [...prev, candidate]);
      }
      
      // Trigger callback if provided
      if (onSubmissionCreated) {
        onSubmissionCreated(updatedSubmission);
      }
      
      return updatedSubmission;
    } else {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to submit candidate');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.jobId || !formData.candidateId) {
      alert('Please select both a job and a candidate.');
      return;
    }

    setLoading(true);
    try {
      const submissionData = {
        candidate_id: formData.candidateId,
        job_id: formData.jobId,
        job_title: jobs.find(j => j.id === formData.jobId)?.title || 'Unknown Job',
        recruiter_id: 'current-user-id', // Would come from auth context
        recruiter_name: formData.addedBy,
        initial_status: formData.status,
        submission_source: formData.source || 'manual',
        submission_notes: formData.comments,
        priority_level: 'normal',
        match_score: 0.85 // Default match score
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        const newSubmission = await response.json();
        onSubmissionCreated(newSubmission);
        onClose();
        resetForm();
      } else {
        alert('Failed to create submission. Please try again.');
      }
    } catch (error) {
      console.error('Error creating submission:', error);
      alert('Error creating submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      addedBy: 'Ramkumar Sankaranarayanan',
      jobId: '',
      candidateId: '',
      status: 'sourced',
      source: '',
      payRate: '',
      billRate: '',
      salary: '',
      comments: ''
    });
    setSelectedCandidate(null);
    setSelectedCandidates([]);
    setCandidateSearchTerm('');
    setJobSearchTerm('');
    // State reset handled by individual AddButton components
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl w-full h-[90vh] flex ${editingSubmission ? 'max-w-3xl' : 'max-w-7xl'}`}>
        {/* Sidebar - Only show in Add mode */}
        {!editingSubmission && (
          <div className="w-80 bg-gray-50 border-r border-gray-200 p-6 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-2">
              <Star className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">
                {editingSubmission ? 'Edit Client Submission' : 'Add Submission'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Fields */}
          <div className="space-y-6 flex-1">
            {/* Added By */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Added By</label>
              <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full">
                <Check className="w-4 h-4" />
                <span className="text-sm">{formData.addedBy}</span>
              </div>
            </div>

            {/* Jobs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Jobs</label>
              <div className="relative">
                {formData.jobId && (
                  <div className="mb-2">
                    <div className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full inline-flex items-center space-x-2">
                      <span className="text-sm">{jobSearchTerm}</span>
                      <button
                        onClick={() => {
                          setFormData(prev => ({ ...prev, jobId: '' }));
                          setJobSearchTerm('');
                        }}
                        className="text-pink-600 hover:text-pink-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={jobSearchTerm}
                  onChange={(e) => {
                    setJobSearchTerm(e.target.value);
                    setShowJobDropdown(true);
                  }}
                  onFocus={() => setShowJobDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {showJobDropdown && filteredJobs.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredJobs.slice(0, 5).map(job => (
                      <button
                        key={job.id}
                        onClick={() => handleJobSelect(job)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0"
                      >
                        <div className="font-medium text-gray-900">{job.id}: {job.title}</div>
                        <div className="text-sm text-gray-500">{job.department} • {job.location}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Candidates */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <span>Candidates</span>
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={candidateSearchTerm}
                  onChange={(e) => {
                    setCandidateSearchTerm(e.target.value);
                    setShowCandidateResults(true);
                  }}
                  onFocus={() => setShowCandidateResults(true)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {submissionStatuses.map(status => (
                    <option key={status} value={status}>
                      {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
              <select
                value={formData.source}
                onChange={(e) => handleInputChange('source', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select source...</option>
                {sources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            {/* Pay Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pay Rate</label>
              <input
                type="number"
                placeholder="$/hour"
                value={formData.payRate}
                onChange={(e) => handleInputChange('payRate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Bill Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bill Rate</label>
              <input
                type="number"
                placeholder="$/hour"
                value={formData.billRate}
                onChange={(e) => handleInputChange('billRate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Salary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Salary</label>
              <input
                type="number"
                placeholder="Annual salary"
                value={formData.salary}
                onChange={(e) => handleInputChange('salary', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Comments</label>
              <textarea
                placeholder="Additional notes..."
                rows={3}
                value={formData.comments}
                onChange={(e) => handleInputChange('comments', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Edit/Add actions now handled in main content area */}
        </div>
        )}

        {/* Main Content Area - Candidate Search Results */}
        <div className="flex-1 p-6">
          {editingSubmission ? (
            /* Edit Mode - Show only message and buttons */
            <div className="flex items-center justify-center min-h-full">
              <div className="max-w-2xl w-full space-y-6">
                {/* Debug info - remove after testing */}
                {console.log('Editing submission:', editingSubmission)}
                
                {/* Submission Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Submission Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Candidate</p>
                      <p className="font-medium">{editingSubmission?.candidate_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Job</p>
                      <p className="font-medium">{editingSubmission?.job_title || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Status</p>
                      <p className="font-medium">{editingSubmission?.status?.replace(/_/g, ' ').toUpperCase() || editingSubmission?.current_status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Submitted By</p>
                      <p className="font-medium">{editingSubmission?.submitted_by_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Submission ID</p>
                      <p className="font-medium text-xs">{editingSubmission?.id || editingSubmission?.submission_id || 'Missing ID'}</p>
                    </div>
                  </div>
                </div>

                {/* Edit Restrictions Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 text-sm mb-1">Edit Restrictions</h4>
                      <p className="text-sm text-blue-800">
                        You can only withdraw this submission from the current selection process. 
                        To submit the candidate again or to a different job, please use the <strong>'Add Submission'</strong> button 
                        to create a new submission.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const submissionId = editingSubmission?.id || editingSubmission?.submission_id;
                      
                      if (!editingSubmission || !submissionId) {
                        alert('Submission data is missing. Please close and try again.');
                        console.error('Missing submission data:', editingSubmission);
                        return;
                      }
                      
                      if (window.confirm('Are you sure you want to withdraw this submission? This action cannot be undone.')) {
                        try {
                          setLoading(true);
                          const response = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submissionId}`,
                            {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ status: 'withdrawn' })
                            }
                          );
                          
                          if (response.ok) {
                            alert('Submission withdrawn successfully');
                            if (onSubmissionCreated) {
                              onSubmissionCreated();
                            }
                            onClose();
                          } else {
                            const errorData = await response.text();
                            alert(`Failed to withdraw submission: ${errorData}`);
                          }
                        } catch (error) {
                          console.error('Error withdrawing submission:', error);
                          alert(`Error withdrawing submission: ${error.message}`);
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    disabled={loading || !editingSubmission}
                    className="flex-1 bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5" />
                        <span>Withdraw Submission</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Add Mode - Show full form */
            <>
          {/* Header with sort options */}
          {(showMatchedCandidates || (showCandidateResults && candidateSearchTerm)) && (
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {showMatchedCandidates && !candidateSearchTerm ? (
                  <>
                    AI-Matched Candidates ({matchedCandidates.length})
                    {loadingMatches && <span className="ml-2 text-sm text-gray-500">Loading...</span>}
                  </>
                ) : (
                  `Candidate Search Results (${filteredCandidates.length})`
                )}
              </h3>
              {showMatchedCandidates && !candidateSearchTerm && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="match_score">Best Match</option>
                    <option value="name">Name</option>
                    <option value="experience">Experience</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Display matched candidates when job is selected and no search term */}
          {showMatchedCandidates && !candidateSearchTerm && !loadingMatches && (
            <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
              {matchedCandidates.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No matching candidates found for this position</p>
                  <p className="text-sm mt-1">Try searching manually using the candidate search above</p>
                </div>
              ) : (
                matchedCandidates.map(candidate => {
                  const isSubmitted = selectedCandidates.some(sc => sc.candidate_id === candidate.candidate_id);
                  const matchScore = candidate.match_score || 0;
                  const matchBreakdown = candidate.match_breakdown || {};
                  const matchReasons = candidate.match_reasons || [];
                  
                  return (
                    <div
                      key={candidate.candidate_id}
                      className={`p-4 border-2 rounded-lg hover:shadow-md transition-all ${
                        isSubmitted ? 'bg-green-50 border-green-300' : 'border-gray-200 hover:border-blue-200'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSubmitted ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <User className={`w-5 h-5 ${
                            isSubmitted ? 'text-green-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1 space-y-3">
                          {/* Header with name and match score */}
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900 text-lg">
                                  {candidate.candidate_name}
                                </h4>
                                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  AI Sourced
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">{candidate.current_title || 'N/A'}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isSubmitted && (
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                                  ✓ Submitted
                                </span>
                              )}
                              <div className={`flex items-center space-x-1 px-3 py-1 rounded-full border ${getMatchScoreColor(matchScore)}`}
                                   title={`AI Match Score: ${matchScore}%`}>
                                {getMatchScoreIcon(matchScore)}
                                <span className="font-bold text-sm">{matchScore}%</span>
                              </div>
                            </div>
                          </div>

                          {/* Match reasons */}
                          {matchReasons.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {matchReasons.map((reason, idx) => (
                                <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                                  {reason}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Candidate details */}
                          <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Building className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{candidate.current_company || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{candidate.location || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Mail className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{candidate.email}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Info className="w-4 h-4 flex-shrink-0" />
                              <span>{candidate.total_experience_years || 0} years exp</span>
                            </div>
                          </div>

                          {/* Match breakdown - expandable tooltip */}
                          <details className="text-xs">
                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                              View match breakdown
                            </summary>
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                              <div className="flex justify-between">
                                <span>Skills Match:</span>
                                <span className="font-medium">{matchBreakdown.skill_match || 0}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Experience Match:</span>
                                <span className="font-medium">{matchBreakdown.experience_match || 0}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Location Match:</span>
                                <span className="font-medium">{matchBreakdown.location_match || 0}%</span>
                              </div>
                            </div>
                          </details>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                            {formData.jobId && !isSubmitted && (
                              <AddButton
                                buttonId={`submit-matched-${candidate.candidate_id}`}
                                onAdd={async (e) => {
                                  if (e && e.stopPropagation) e.stopPropagation();
                                  // Create a candidate object compatible with handleSubmitCandidate
                                  const candidateObj = {
                                    id: candidate.candidate_id,
                                    first_name: candidate.candidate_name.split(' ')[0],
                                    last_name: candidate.candidate_name.split(' ').slice(1).join(' '),
                                    email: candidate.email,
                                    phone: candidate.phone,
                                    current_company: candidate.current_company,
                                    location: candidate.location,
                                    current_title: candidate.current_title
                                  };
                                  return handleSubmitCandidate(candidateObj);
                                }}
                                addText="Submit Candidate"
                                addingText="Submitting..."
                                addedText="Submitted"
                                size="sm"
                                variant="primary"
                                icon={Send}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Display search results when searching */}
          {showCandidateResults && candidateSearchTerm && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Candidate Search Results ({filteredCandidates.length})
              </h3>
              <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                {filteredCandidates.map(candidate => {
                  const isSubmitted = selectedCandidates.some(sc => sc.id === candidate.id);
                  // isSubmitting now handled by AddButton component
                  
                  return (
                    <div
                      key={candidate.id}
                      className={`p-4 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                        selectedCandidate?.id === candidate.id ? 'bg-blue-50 border-blue-300' : 
                        isSubmitted ? 'bg-green-50 border-green-300' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSubmitted ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <User className={`w-5 h-5 ${
                            isSubmitted ? 'text-green-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">
                                {candidate.first_name} {candidate.last_name}
                              </h4>
                              {(candidate.source === 'ai' || candidate.source === 'ai_sourcing') && (
                                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  AI Sourced
                                </span>
                              )}
                            </div>
                            {isSubmitted && (
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                ✓ Submitted
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Building className="w-4 h-4" />
                              <span>{candidate.current_company || 'Apple'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4" />
                              <span>{candidate.location || 'Dallas'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Mail className="w-4 h-4" />
                              <span>{candidate.email}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4" />
                              <span>{candidate.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Info className="w-4 h-4" />
                              <span>{candidate.status || 'New Lead'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <User className="w-4 h-4" />
                              <span>{candidate.recruiter_name || 'Srinivas Puppala'}</span>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCandidateSelect(candidate);
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Select
                            </button>
                            {formData.jobId && !isSubmitted && (
                              <AddButton
                                buttonId={`submit-candidate-${candidate.id}`}
                                onAdd={async (e) => {
                                  if (e && e.stopPropagation) e.stopPropagation();
                                  return handleSubmitCandidate(candidate);
                                }}
                                addText="Submit"
                                addingText="Submitting..."
                                addedText="Submitted"
                                size="sm"
                                variant="primary"
                                icon={Send}
                              />
                            )}
                            {!formData.jobId && (
                              <span className="text-gray-500 text-sm">Select a job first</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!showCandidateResults && !showMatchedCandidates && (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">Select a job to see AI-matched candidates</p>
                <p className="text-sm">or search for candidates manually</p>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddSubmissionModal;